import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { api, ApiError, basePath, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert } from '../layout';

/**
 * Apple and Google on the web pages. The buttons are plain links: the API sends the browser to Apple / Google
 * and takes it back (no script of theirs runs in the panel). Not offered through a connected panel (the return
 * address is the app's own domain).
 */

type Provider = 'apple' | 'google';
const NAMES: Record<Provider, string> = { apple: 'Apple', google: 'Google' };

export function useProviders() {
  return useQuery({
    queryKey: ['oauth-providers'], staleTime: Infinity, retry: false, enabled: !basePath,
    queryFn: () => api.get<Record<Provider, boolean>>('api/v1/auth/providers'),
  });
}

function Logo({ provider }: { provider: Provider }) {
  return provider === 'apple' ? (
    <svg viewBox="0 0 17 20" width="15" height="18" aria-hidden="true"><path fill="currentColor" d="M14.1 10.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9C3.6 4.8 2 5.8 1.1 7.4c-1.8 3.2-.5 7.9 1.3 10.5.9 1.3 1.9 2.7 3.2 2.6 1.3-.1 1.8-.8 3.3-.8 1.6 0 2 .8 3.4.8 1.4 0 2.3-1.3 3.1-2.6 1-1.5 1.4-2.9 1.4-3-.1 0-2.7-1-2.7-4.3ZM11.6 3c.7-.9 1.2-2 1-3.1-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4Z" /></svg>
  ) : (
    <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <path fill="#4285F4" d="M17.6 9.2c0-.6-.1-1.2-.2-1.8H9v3.4h4.8a4.1 4.1 0 0 1-1.8 2.7v2.2h2.9c1.7-1.6 2.7-3.9 2.7-6.5Z" />
      <path fill="#34A853" d="M9 18c2.4 0 4.5-.8 6-2.2l-2.9-2.2c-.8.5-1.8.9-3.1.9-2.4 0-4.4-1.6-5.1-3.7H.9v2.3A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.9 10.8a5.4 5.4 0 0 1 0-3.6V4.9H.9a9 9 0 0 0 0 8.2l3-2.3Z" />
      <path fill="#EA4335" d="M9 3.6c1.3 0 2.5.5 3.5 1.4l2.6-2.6A9 9 0 0 0 .9 4.9l3 2.3C4.6 5.1 6.6 3.6 9 3.6Z" />
    </svg>
  );
}

/** "Prisijungti su Apple / Google": to sign in (or register), or to accept an invitation (`invite`). */
export function SignInWith({ remember = false, invite }: { remember?: boolean; invite?: string }) {
  const { t } = useI18n();
  const providers = useProviders();
  const on = (['apple', 'google'] as Provider[]).filter((p) => providers.data?.[p]);
  if (!on.length) return null;
  const query = invite ? `invite=${encodeURIComponent(invite)}` : `remember=${remember ? 1 : 0}`;
  return (
    <div className="oauth-buttons">
      {on.map((p) => (
        <a key={p} className={`oauth-button oauth-button--${p}`} href={`api/v1/auth/oauth/${p}/start?${query}`}>
          <Logo provider={p} /><span>{t('Tęsti su {provider}', { provider: NAMES[p] })}</span>
        </a>
      ))}
      <p className="oauth-or"><span>{t('arba')}</span></p>
    </div>
  );
}

/** The message for `?oauth=…` after coming back from Apple / Google (nothing when the person cancelled). */
export function oauthMessage(t: ReturnType<typeof useI18n>['t'], code: string | undefined): string | null {
  if (!code || code === 'cancelled' || code === 'linked') return null;
  return errorText(t, new ApiError(400, code));
}

interface Account { hasPassword: boolean; identities: { provider: Provider; email: string | null; since: string }[] }

/** Nustatymai → Saugumas: the ways in of the account (password, Apple, Google). */
export function WaysInCard({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const providers = useProviders();
  const account = useQuery({ queryKey: ['account'], queryFn: () => api.get<Account>('api/v1/account') });
  const unlink = useMutation({
    mutationFn: (p: Provider) => api.delete(`api/v1/account/identities/${p}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account'] }),
  });
  const back = new URLSearchParams(window.location.search).get('oauth') ?? undefined;
  const a = account.data;
  if (basePath || me.viewAsAdmin) return null;
  return (
    <section className="panel-card">
      <p className="eyebrow">{t('Prisijungimas')}</p><h2>{t('Prisijungimo būdai')}</h2>
      <p className="muted">{t('Prie tos pačios paskyros galite jungtis keliais būdais. Lieka bent vienas.')}</p>
      {back === 'linked' ? <Alert kind="success">{t('Prisijungimo būdas susietas.')}</Alert> : null}
      {oauthMessage(t, back) ? <Alert kind="error">{oauthMessage(t, back)}</Alert> : null}
      {unlink.isError ? <Alert kind="error">{errorText(t, unlink.error)}</Alert> : null}
      <ul className="team-list">
        <li>
          <div className="customer-avatar">✉</div>
          <div className="team-who"><strong>{t('El. paštas ir slaptažodis')}</strong><small>{me.user.email}</small></div>
          <span className="badge">{a?.hasPassword ? t('Nustatytas') : t('Nenustatytas')}</span>
        </li>
        {(['apple', 'google'] as Provider[]).filter((p) => providers.data?.[p] || a?.identities.some((i) => i.provider === p)).map((p) => {
          const linked = a?.identities.find((i) => i.provider === p);
          return (
            <li key={p}>
              <div className="customer-avatar"><Logo provider={p} /></div>
              <div className="team-who"><strong>{NAMES[p]}</strong><small>{linked ? linked.email ?? t('Susieta') : t('Nesusieta')}</small></div>
              {linked ? (
                <button type="button" className="button button--ghost button--small" disabled={unlink.isPending}
                  onClick={() => { if (confirm(t('Atsieti {provider}? Prisijungti su juo nebegalėsite.', { provider: NAMES[p] }))) unlink.mutate(p); }}>{t('Atsieti')}</button>
              ) : <a className="button button--ghost button--small" href={`api/v1/auth/oauth/${p}/link`}>{t('Susieti')}</a>}
            </li>
          );
        })}
      </ul>
      {a && !a.hasPassword ? <p className="muted account-note">{t('Slaptažodį galite nustatyti per „Pamiršote slaptažodį?“ prisijungimo puslapyje.')}</p> : null}
    </section>
  );
}

/** Nustatymai → Duomenys ir paskyra: deleting one's own account (not the company). */
export function DeleteMyAccount({ me }: { me: Me }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const account = useQuery({ queryKey: ['account'], queryFn: () => api.get<Account>('api/v1/account') });
  const remove = useMutation({
    mutationFn: (body: { password?: string; confirm?: string }) => api.post('api/v1/account/delete', body),
    onSuccess: async () => { queryClient.clear(); await navigate({ to: '/login', search: { closed: 1 } }); },
  });
  if (me.viewAsAdmin) return null;
  const word = 'IŠTRINTI';
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    remove.mutate(account.data?.hasPassword ? { password: String(d.get('password')) } : { confirm: String(d.get('confirm')).trim().toUpperCase() });
  };
  return (
    <section className="panel-card account-data">
      <p className="eyebrow">{t('Mano paskyra')}</p><h2>{t('Ištrinti mano paskyrą')}</h2>
      <details className="account-close">
        <summary>{t('Ištrinti mano paskyrą')}</summary>
        <p>{t('Ištrinama jūsų prisijungimo paskyra: vardas, el. paštas, prisijungimo būdai ir sesijos. Įmonės, kuriose dirbate su kitais, lieka; jei esate vienintelis savininkas, pirmiausia paskirkite kitą savininką. Įmonė, kurioje esate vienas, uždaroma; jos apskaitos dokumentai saugomi įstatymo nustatytą laiką.')}</p>
        {remove.isError ? <Alert kind="error">{errorText(t, remove.error)}</Alert> : null}
        <form onSubmit={submit}>
          {account.data?.hasPassword
            ? <label>{t('Slaptažodis')}<input type="password" name="password" autoComplete="current-password" required /></label>
            : <label>{t('Įrašykite {word}', { word })}<input name="confirm" autoComplete="off" required pattern={`${word}|${word.toLowerCase()}`} /></label>}
          <button className="button button--danger" disabled={remove.isPending || !account.data}>{t('Ištrinti paskyrą')}</button>
        </form>
      </details>
    </section>
  );
}

interface JoinSettings { login: string | null; role: 'member' | 'viewer'; enabled: boolean; hasPassword: boolean }

/** Nustatymai → Komanda (owner): the company's login and password for joining it. */
export function JoinSettingsCard({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['company-join'], queryFn: () => api.get<{ join: JoinSettings }>('api/v1/company/join').then((r) => r.join) });
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (body: object) => api.put('api/v1/company/join', body),
    onSuccess: async () => { setSaved(true); await queryClient.invalidateQueries({ queryKey: ['company-join'] }); },
  });
  const j = settings.data;
  if (me.company.role !== 'owner' || me.viewAsAdmin || !j) return null;
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaved(false);
    const d = new FormData(e.currentTarget);
    const password = String(d.get('password') ?? '');
    save.mutate({ login: String(d.get('login')).trim().toLowerCase(), role: d.get('role'), enabled: d.get('enabled') === '1', ...(password ? { password } : {}) });
    e.currentTarget.querySelector<HTMLInputElement>('input[name=password]')!.value = '';
  };
  return (
    <section className="panel-card">
      <p className="eyebrow">{t('Prisijungimas prie įmonės')}</p><h2>{t('Įmonės prisijungimo vardas ir slaptažodis')}</h2>
      <p className="muted">{t('Darbuotojai gali prisijungti prie įmonės patys: registruodamiesi arba programėlėje įveda šį vardą ir slaptažodį. Visi prisijungę matomi sąraše aukščiau ir gali būti pašalinti. Pakeitus slaptažodį senasis nebegalioja.')}</p>
      {saved ? <Alert kind="success">{t('Išsaugota.')}</Alert> : null}
      {save.isError ? <Alert kind="error">{errorText(t, save.error)}</Alert> : null}
      <form onSubmit={submit} key={`${j.login}-${j.enabled}-${j.role}`}>
        <div className="form-grid">
          <label>{t('Įmonės prisijungimo vardas')}<input name="login" defaultValue={j.login ?? ''} required minLength={3} maxLength={40} pattern="[a-zA-Z0-9][a-zA-Z0-9._\-]{2,39}" autoComplete="off" /></label>
          <label>{j.hasPassword ? t('Naujas įmonės slaptažodis (nebūtina)') : t('Įmonės slaptažodis')}<input type="password" name="password" minLength={10} maxLength={200} autoComplete="new-password" required={!j.hasPassword} /></label>
          <label>{t('Prisijungusiųjų teisės')}<select name="role" defaultValue={j.role}>
            <option value="member">{t('Narys')}</option><option value="viewer">{t('Tik peržiūra')}</option>
          </select></label>
        </div>
        <label className="remember-device"><input type="checkbox" name="enabled" value="1" defaultChecked={j.enabled} /><span>{t('Leisti prisijungti su įmonės vardu ir slaptažodžiu')}</span></label>
        <div className="form-actions"><button className="button button--primary" disabled={save.isPending}>{t('Išsaugoti')}</button></div>
      </form>
    </section>
  );
}
